import React, { useEffect, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Card, CardHeader, CardContent } from '../../../../components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';
import Pagination from '../../../../components/Pagination';
import { useTranslation } from 'react-i18next';
import { UserEducationInput, UserEducationView } from '../../../../models/employee';
import { addUserEducation, deleteUserEducation, editUserEducation, getUserEducations } from '../../../../api/Employee/education';
import EducationForm from './educationInputForm';
import { formatDate } from '../../../../utils';

interface Props { userId: string; }

const EducationSection: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [educations, setEducations] = useState<UserEducationView[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [educationInput, setEducationInput] = useState<UserEducationInput>({
    id: 0,
    userId: userId,
    institutionName: '', degree: '', fieldOfStudy: '', startDate: undefined, endDate: undefined, grade: ''
  });

  const load = async () => {
    try {
      setLoading(true);
      const res = await getUserEducations(userId);
      setEducations(res?.payload ?? []);
      setPage(1);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [userId]);

  const handleOpenAdd = () => { setEducationInput({ id:0, userId, institutionName:'', degree:'', fieldOfStudy:'', startDate:undefined, endDate:undefined, grade:'' }); setAddOpen(true); };
  const handleAdd = async () => { setLoading(true); await addUserEducation(educationInput); setAddOpen(false); await load(); setLoading(false); };
  const handleOpenEdit = (e: UserEducationView) => { setEducationInput({ id:e.id, userId, institutionName:e.institutionName, degree:e.degree, fieldOfStudy:e.fieldOfStudy, startDate:e.startDate, endDate:e.endDate, grade:e.grade }); setEditOpen(true); };
  const handleEdit = async () => { setLoading(true); await editUserEducation(educationInput.id, educationInput); setEditOpen(false); await load(); setLoading(false); };
  const handleOpenDelete = (e: UserEducationView) => { setEducationInput({ id:e.id, userId, institutionName:e.institutionName, degree:e.degree, fieldOfStudy:e.fieldOfStudy, startDate:e.startDate, endDate:e.endDate, grade:e.grade }); setDeleteOpen(true); };
  const handleDelete = async () => { setLoading(true); await deleteUserEducation(educationInput.id); setDeleteOpen(false); await load(); setLoading(false); };

  return (
    <Card variant="elevated" className="mb-2">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AppIcon name="GraduationCap" className="text-primary" />
          <span className="text-muted-foreground font-semibold">{t('EDUCATION')}</span>
        </div>
        <Button size="sm" iconName="Plus" onClick={handleOpenAdd} disabled={loading}>{t('Add')}</Button>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="p-2">
            {[...Array(3)].map((_, i) => <div key={i} className={`animate-pulse bg-muted h-8 rounded${i < 2 ? ' mb-1' : ''}`} />)}
          </div>
        )}
        {!loading && educations.length === 0 && (
          <div className="text-center py-10">
            <AppIcon name="GraduationCap" size={32} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">{t('No education records found')}</p>
          </div>
        )}
        <div className="space-y-3">
          {educations.slice((page - 1) * pageSize, page * pageSize).map(e => (
            <article key={e.id} className="bg-muted/50 border border-border/40 rounded-lg p-4 group">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3 min-w-0">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mt-0.5">
                    <AppIcon name="GraduationCap" size={18} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-foreground truncate">{e.institutionName}</h4>
                    <p className="text-sm text-muted-foreground">
                      {[e.degree, e.fieldOfStudy].filter(Boolean).join(' in ')}
                      {e.grade && <span className="ms-2 text-xs bg-primary/10 text-primary rounded px-1.5 py-0.5">{t('Grade')}: {e.grade}</span>}
                    </p>
                    {(e.startDate || e.endDate) && (
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {e.startDate ? formatDate(new Date(e.startDate)) : ''}
                        {e.startDate && e.endDate ? ' - ' : ''}
                        {e.endDate ? formatDate(new Date(e.endDate)) : ''}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <TooltipPrimitive.Provider delayDuration={300}>
                    <TooltipPrimitive.Root>
                      <TooltipPrimitive.Trigger asChild>
                        <Button variant="ghost" size="icon" className="text-primary h-7 w-7" onClick={() => handleOpenEdit(e)}>
                          <AppIcon name="Pencil" size={14} />
                        </Button>
                      </TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content className="rounded bg-foreground px-2 py-1 text-xs text-background shadow-md" sideOffset={5}>
                          {t('Edit')}
                          <TooltipPrimitive.Arrow className="fill-foreground" />
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </TooltipPrimitive.Root>
                    <TooltipPrimitive.Root>
                      <TooltipPrimitive.Trigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => handleOpenDelete(e)}>
                          <AppIcon name="Trash2" size={14} />
                        </Button>
                      </TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content className="rounded bg-foreground px-2 py-1 text-xs text-background shadow-md" sideOffset={5}>
                          {t('Delete')}
                          <TooltipPrimitive.Arrow className="fill-foreground" />
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </TooltipPrimitive.Root>
                  </TooltipPrimitive.Provider>
                </div>
              </div>
            </article>
          ))}
        </div>
        {!loading && educations.length > pageSize && (
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(educations.length / pageSize)}
            onPageChange={setPage}
          />
        )}

        {/* Add Dialog */}
        <Dialog open={addOpen} onOpenChange={(isOpen) => { if (!isOpen) setAddOpen(false); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Add Education')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <EducationForm educationInput={educationInput} setEducationInput={setEducationInput} loading={loading} t={t} />
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>{t('Cancel')}</Button>
              <Button loading={loading} onClick={handleAdd}>{t('Add')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={(isOpen) => { if (!isOpen) setEditOpen(false); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Edit Education')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <EducationForm educationInput={educationInput} setEducationInput={setEducationInput} loading={loading} t={t} />
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>{t('Cancel')}</Button>
              <Button loading={loading} onClick={handleEdit}>{t('Save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteOpen} onOpenChange={(isOpen) => { if (!isOpen) setDeleteOpen(false); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Delete Education')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p>{t('Are you sure you want to delete this education?')}</p>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('Cancel')}</Button>
              <Button variant="destructive" loading={loading} onClick={handleDelete}>{t('Delete')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default EducationSection;
